/**
 * Decision submission hook for the Quick Review detail modal.
 * @module useQuickReviewDecision
 */
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { QuickReviewService } from "../../../../api/services/quickReviewService";
import {
  QuickReviewDecision,
  QuickReviewDecisionResult,
} from "../../../../data-model/quickReviewModel";
import { RootState } from "../../../../redux/store/store";

/** Request lifecycle state for a Quick Review decision submission. */
export interface QuickReviewDecisionState {
  /** True while the decision endpoint request is in flight */
  submitting: boolean;
  /** Safe error message when the decision request fails */
  error: string | null;
  /** Board audit result returned on success */
  result: QuickReviewDecisionResult | null;
}

const INITIAL_DECISION_STATE: QuickReviewDecisionState = {
  submitting: false,
  error: null,
  result: null,
};

/**
 * Applies a reviewer's decision to the boarded order shown in the Quick
 * Review modal. Reads the order id from the modal's redux metadata and
 * calls the decision endpoint, exposing submitting/error state plus the
 * applied result for the modal's footer actions.
 *
 * @param onSuccess - Optional callback fired after the decision succeeds,
 * receiving the board audit result
 * @param onError - Optional callback fired when the decision request fails,
 * receiving the error message
 * @returns Decision state plus handlers for the two footer actions:
 * "No Action Needed" (save) and "Reprocess Order" (cancel)
 */
export const useQuickReviewDecision = (
  onSuccess?: (result: QuickReviewDecisionResult) => void,
  onError?: (error: string) => void,
) => {
  const { metaData } = useSelector((state: RootState) => state.modal);
  const orderId = metaData?.orderId ?? "";
  const [decisionState, setDecisionState] = useState<QuickReviewDecisionState>(
    INITIAL_DECISION_STATE,
  );

  const submitDecision = useCallback(
    async (
      decision: QuickReviewDecision,
      decisionPayload?: Record<string, unknown>,
    ) => {
      if (!orderId || decisionState.submitting) return;

      setDecisionState({ submitting: true, error: null, result: null });

      try {
        const result = await QuickReviewService.decision({
          order_id: orderId,
          decision,
          decision_payload: decisionPayload ?? null,
        });
        setDecisionState({ submitting: false, error: null, result });
        onSuccess?.(result);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        setDecisionState({
          submitting: false,
          error: message,
          result: null,
        });
        onError?.(message);
      }
    },
    [orderId, decisionState.submitting, onSuccess, onError],
  );

  /** "No Action Needed" save button: accept the order as-is. */
  const handleNoActionNeeded = useCallback(
    () => submitDecision(QuickReviewDecision.NO_ACTION_NEEDED),
    [submitDecision],
  );

  /** "Reprocess Order" cancel button: send the order back through the pipeline. */
  const handleReprocess = useCallback(
    () => submitDecision(QuickReviewDecision.REPROCESS),
    [submitDecision],
  );

  return {
    submitting: decisionState.submitting,
    error: decisionState.error,
    result: decisionState.result,
    handleNoActionNeeded,
    handleReprocess,
  };
};
